"use client";

import { useCallback, useRef } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { getErrorMessage } from "@/lib/aicheck/error-utils";
import { writeMergeCheckResultCache } from "@/lib/aicheck/merge-check-cache";
import { authoritativeNodeStates } from "@/lib/aicheck/node-execution";
import { withProjectDataComparison } from "@/lib/aicheck/project-data-comparison";
import { isSameProjectDataSource } from "@/lib/aicheck/project-data-source";
import { deriveRunFailureSummary } from "@/lib/aicheck/run-failure-summary";
import { readNDJSONStream } from "@/lib/ndjson-reader";
import { authService } from "@/services/auth";
import { type AICheckState, useAICheckStore } from "@/stores/aicheck";
import type {
  AdminControlSuccessResponse,
  AdminNodeAction,
  AggregatedProjectData,
  AICheckResult,
  AICheckRunCheckpoint,
  AICheckRunResult,
  CacheStaleReason,
  CheckpointAIResultEntry,
  CheckpointSignedValue,
  ConsistencyCheckResult,
  ContactTopicsResult,
  CustomerProfileResult,
  MergeCheckCacheMetadata,
  NodeExecutionDirective,
  OutputLanguage,
  PipelineNodeMembers,
  PipelineNodeRuntimeStatus,
  ProjectDataComparisonSnapshot,
  ProjectDataSourceDescriptor,
  RunConflictResponse,
  RunProgress,
  RunStatus,
  SkippedItem,
} from "@/types/aicheck";
import { useLocalResults } from "./useLocalResults";
import { useRunCheckpoint } from "./useRunCheckpoint";

export interface RunCheckOptions {
  mode?: "crm" | "single-project-data";
  conversionId?: string;
  projectData?: AggregatedProjectData;
  projectDataSource?: ProjectDataSourceDescriptor;
  projectDataComparison?: ProjectDataComparisonSnapshot;
  mergeCheckCache?: MergeCheckCacheMetadata;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function reconstructProgressFromCache(cached: AICheckRunResult) {
  const aiResultNames = [...new Set(cached.aiResults.map((r) => r.checkName))];
  const plannedChecks = [...new Set(cached.plannedChecks ?? [])];
  const plannedCheckIds = [...new Set(cached.plannedCheckIds ?? [])];
  const checkBatchGroups = cached.checkBatchGroups ?? [];
  const checkLaneMap: Record<string, "independent" | "dependent" | "afterAll"> =
    cached.checkLaneMap ? { ...cached.checkLaneMap } : {};

  const completedCheckNames = [
    ...new Set(
      cached.aiResults
        .filter((r) => r.status !== "skipped")
        .map((r) => r.checkName),
    ),
  ];

  const { failedChecks, failedStage, stageError } =
    deriveRunFailureSummary(cached);
  const plannedCheckNameById = cached.plannedCheckNameById as Record<
    string,
    string
  >;
  const checkIdByNameFromCache = new Map<string, string>();
  for (let i = 0; i < plannedChecks.length; i++) {
    const checkName = plannedChecks[i];
    const checkId = plannedCheckIds[i];
    if (checkName && checkId && plannedCheckNameById[checkId] === checkName) {
      checkIdByNameFromCache.set(checkName, checkId);
    }
  }

  const skippedByName = new Set<string>();
  for (const r of cached.aiResults) {
    if (r.status === "skipped") skippedByName.add(r.checkName);
  }
  for (const name of plannedChecks) {
    if (!aiResultNames.includes(name)) skippedByName.add(name);
  }
  const skippedItems = [...skippedByName]
    .map((name) => {
      const checkId = checkIdByNameFromCache.get(name);
      if (!checkId) return null;
      return {
        id: checkId,
        name,
        kind: "ai-check" as const,
      };
    })
    .filter((item): item is { id: string; name: string; kind: "ai-check" } =>
      Boolean(item),
    );

  return {
    plannedChecks,
    checkBatchGroups:
      checkBatchGroups.length > 0 ? checkBatchGroups : undefined,
    checkLaneMap,
    completedCheckNames,
    failedChecks,
    skippedItems: skippedItems.length > 0 ? skippedItems : undefined,
    failedStage,
    stageError,
  };
}

function deriveCheckpointConsistencySummaries(
  checkpoint: AICheckRunCheckpoint,
): Pick<
  RunProgress,
  "consistencyRuleResults" | "profileConsistencyRuleResults"
> {
  const independentRuleIds = new Set(
    checkpoint.nodeMembers?.["consistency-independent"]?.ruleIds ?? [],
  );
  const profileRuleIds = new Set(
    checkpoint.nodeMembers?.["consistency-profile"]?.ruleIds ?? [],
  );

  return {
    consistencyRuleResults: checkpoint.consistencyResults
      .filter((result) => independentRuleIds.has(result.ruleId))
      .map((result) => ({
        name: result.ruleName,
        passed: result.passed,
      })),
    profileConsistencyRuleResults: checkpoint.consistencyResults
      .filter((result) => profileRuleIds.has(result.ruleId))
      .map((result) => ({
        name: result.ruleName,
        passed: result.passed,
      })),
  };
}

function applyCheckpointToStore(
  projectId: number,
  language: OutputLanguage,
  checkpoint: AICheckRunCheckpoint,
  options?: {
    status?: RunStatus;
    currentStep?: string;
    nodeExecutionDirectives?: NodeExecutionDirective[];
    clearCurrentResult?: boolean;
  },
): void {
  const plannedChecks = (checkpoint.plannedCheckIds ?? []).map(
    (checkId) => checkpoint.plannedCheckNameById?.[checkId] ?? checkId,
  );
  const completedCheckNames = [
    ...new Set(
      checkpoint.aiResults
        .filter((entry) => entry.result.status !== "skipped")
        .map((entry) => entry.result.checkName),
    ),
  ];
  const consistencySummaries = deriveCheckpointConsistencySummaries(checkpoint);

  useAICheckStore.setState((state) => {
    const sameCurrentResult =
      state.currentResult?.projectId === projectId &&
      state.currentResult?.language === language
        ? state.currentResult
        : null;

    return {
      currentResult:
        options?.clearCurrentResult && sameCurrentResult
          ? null
          : state.currentResult,
      previousResult:
        options?.clearCurrentResult && sameCurrentResult
          ? sameCurrentResult
          : state.previousResult,
      partialRunData: null,
      runProgress: {
        ...state.runProgress,
        status: options?.status ?? state.runProgress.status,
        currentStep: options?.currentStep ?? state.runProgress.currentStep,
        error: undefined,
        apiStatus: undefined,
        apiProgress: undefined,
        activeChecks: undefined,
        plannedChecks,
        completedCheckNames,
        completedChecks: completedCheckNames.length,
        totalChecks: plannedChecks.length,
        checkLaneMap: checkpoint.checkLaneMap,
        checkBatchGroups: checkpoint.checkBatchGroups,
        consistencyRuleResults: consistencySummaries.consistencyRuleResults,
        profileConsistencyRuleResults:
          consistencySummaries.profileConsistencyRuleResults,
        graphSkeleton: checkpoint.graphSkeleton,
        nodeMembers: checkpoint.nodeMembers,
        failedChecks: checkpoint.failedChecks,
        skippedItems: checkpoint.skippedItems,
        failedStage: checkpoint.failedStage,
        stageError: undefined,
        plannedCheckIds: checkpoint.plannedCheckIds,
        plannedCheckNameById: checkpoint.plannedCheckNameById,
        nodeStates: checkpoint.nodeStates,
        nodeExecutionDirectives:
          options?.nodeExecutionDirectives ??
          checkpoint.nodeExecutionDirectives ??
          [],
        nodeControlVersion: checkpoint.nodeControlVersion ?? 0,
        scheduledAdminControl: undefined,
        lastRecoveryError: undefined,
        selectiveRerun: undefined,
      },
    };
  });
}

function toStaleNodeExecutionDirectives(
  directives: NodeExecutionDirective[] | undefined,
): NodeExecutionDirective[] {
  return (directives ?? []).map((directive) => ({
    ...directive,
    state: "stale" as const,
  }));
}

export function resolveRunStartNodeExecutionState(params: {
  startedAt: string;
  currentStartedAt?: string;
  currentNodeStates?: Record<string, PipelineNodeRuntimeStatus>;
  currentNodeExecutionDirectives?: NodeExecutionDirective[];
  currentNodeControlVersion?: number;
  checkpoint?: AICheckRunCheckpoint | null;
}): {
  nodeStates: Record<string, PipelineNodeRuntimeStatus>;
  nodeExecutionDirectives: NodeExecutionDirective[];
  nodeControlVersion: number;
} {
  const hasCurrentState =
    params.currentStartedAt === params.startedAt &&
    (params.currentNodeStates != null ||
      params.currentNodeExecutionDirectives != null ||
      params.currentNodeControlVersion != null);

  if (hasCurrentState) {
    return {
      nodeStates: params.currentNodeStates ?? {},
      nodeExecutionDirectives: params.currentNodeExecutionDirectives ?? [],
      nodeControlVersion: params.currentNodeControlVersion ?? 0,
    };
  }

  if (params.checkpoint?.startedAt === params.startedAt) {
    return {
      nodeStates: params.checkpoint.nodeStates ?? {},
      nodeExecutionDirectives: params.checkpoint.nodeExecutionDirectives ?? [],
      nodeControlVersion: params.checkpoint.nodeControlVersion ?? 0,
    };
  }

  // **再開の引き継ぎを消さない。** 再開ではサーバが新しい startedAt の run を
  // 作るので、ここは「この run の状態は知らない」に落ちる。だが呼び出し元は
  // 直前にチェックポイントから表示を組み直しており、引き継いだノードの状態は
  // 今も正しい（サーバはそれらを再実行しないので、改めては配らない）。
  // 空で上書きすると、引き継いだ先頭のノード（sys-fetch / sys-compaction /
  // sys-profile。ほかに情報源が無い）が灰色に戻る。
  //
  // 新しい実行では呼び出し元が startRerun / reset で先に捨てているため、
  // ここに残っている状態は「引き継ぎ」しかありえない。
  if (
    params.currentNodeStates &&
    Object.keys(params.currentNodeStates).length > 0
  ) {
    return {
      nodeStates: params.currentNodeStates,
      nodeExecutionDirectives: params.currentNodeExecutionDirectives ?? [],
      nodeControlVersion: params.currentNodeControlVersion ?? 0,
    };
  }

  return {
    nodeStates: {},
    nodeExecutionDirectives: [],
    nodeControlVersion: 0,
  };
}

/**
 * 失敗応答から表示できるメッセージを取り出す。本文が JSON でないことも
 * あるので、読めなければ状態コードで代用する。
 */
async function readErrorMessage(response: Response): Promise<string> {
  const errData = (await response.json().catch(() => ({}))) as {
    error?: unknown;
  };
  return String(errData.error ?? `Server error: ${response.status}`);
}

/**
 * `POST /api/aicheck/runs` へ実行を頼み、runId を受け取るまで。
 *
 * **キャッシュ整合性エラーの再試行を一度だけ書く。** 通常実行と選択再実行が
 * 同じ 2 回ループを別々に持っており、片方だけ直る形になっていた（実際に
 * 「何を消すか」が既に食い違っていた）。持ち込みを落として組み直すのは
 * `build(useCachedPayload)` の仕事、消すのは `onCacheIntegrityFailure` の
 * 仕事にして、往復そのものはここに一つだけ置く。
 */
async function startRunRequest<Meta>(params: {
  token: string;
  signal: AbortSignal;
  build: (useCachedPayload: boolean) => {
    body: Record<string, unknown>;
    meta: Meta;
  };
  onCacheIntegrityFailure: () => void;
  failureMessage: string;
}): Promise<{ runId: string; started: Record<string, unknown>; meta: Meta }> {
  let useCachedPayload = true;
  let response: Response | null = null;
  let meta!: Meta;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    const built = params.build(useCachedPayload);
    meta = built.meta;

    // **開始と観測を分けた入口へ送る。** 旧 `/api/aicheck/run` は開始と進捗が
    // 一本の接続に同居していたため、ブラウザを閉じると実行が消え、MCP や
    // 別ブラウザからは同じ実行を観測できなかった。
    response = await fetch(apiUrl("/api/aicheck/runs"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
      },
      body: JSON.stringify(built.body),
      signal: params.signal,
    });
    if (response.ok) break;

    const message = await readErrorMessage(response);
    if (
      useCachedPayload &&
      isCacheIntegrityVerificationFailure(response.status, message)
    ) {
      params.onCacheIntegrityFailure();
      if (params.signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      useCachedPayload = false;
      continue;
    }
    throw new Error(message);
  }

  if (!response?.ok) throw new Error(params.failureMessage);

  const started = (await response.json()) as Record<string, unknown>;
  const runId = String(started.runId ?? "");
  if (!runId) throw new Error("No runId returned from server");
  return { runId, started, meta };
}

/**
 * ノード状態の「キーフレーム」を運ぶイベントから、適用してよい値だけを取り出す。
 *
 * **空のマップは「情報が無い」であって「何も走っていない」ではない。**
 * サーバは実行状態レジストリを持っていないことがある（run-start 前・15分の
 * TTL 失効・完了後の掃除・中断時に catch がレジストリを捨てたあと）。その
 * `{}` を現在値として適用すると、届いていた状態が消える。しかも state-mapper
 * は「1 件でもキーがあれば全体を信頼する」ため、消えた直後に 1 件入ると部分
 * マップが全体像として扱われ、そこに含まれないノードだけが灰色に戻る。
 *
 * この規則は `stream-synced` と `run-aborted` の両方に要る。二度書くと片方
 * だけ直る（実際に二度に分けて直すことになった）ので、ここに一つだけ置く。
 */
function parseNodeStateKeyframe(event: Record<string, unknown>): {
  nodeStates?: Record<string, PipelineNodeRuntimeStatus>;
  nodeExecutionDirectives?: NodeExecutionDirective[];
  nodeControlVersion?: number;
} {
  // サーバが「ノード制御は使えない」と言っているなら、状態も名乗っていない。
  // 空・不在の扱いは authoritativeNodeStates に一本化する。
  return {
    nodeStates:
      event.nodeControlAvailable === false
        ? undefined
        : authoritativeNodeStates(event.nodeStates),
    nodeExecutionDirectives: Array.isArray(event.nodeExecutionDirectives)
      ? (event.nodeExecutionDirectives as NodeExecutionDirective[])
      : undefined,
    nodeControlVersion:
      typeof event.nodeControlVersion === "number"
        ? event.nodeControlVersion
        : undefined,
  };
}

// ─── Event handler ───────────────────────────────────────────────────

/**
 * 実行の観測を、開始した本人からも「途中から見に来た人」からも同じ経路で
 * 行うための調整点。両者の違いは、表示状態を組み直す必要があるか・最初の
 * 接続を「再接続」と呼ぶか・結果をどのキャッシュへ書くか、だけになる。
 */
interface ObserveRunProgressOptions {
  /** 呼び出し側が既に表示状態を組み直しているなら false。 */
  resetProgress?: boolean;
  /** 自分で始めた実行の初回接続では false（まだ切れていない）。 */
  announceReconnecting?: boolean;
  projectDataComparison?: ProjectDataComparisonSnapshot;
  mergeCheckCache?: MergeCheckCacheMetadata;
  saveContactTopics?: boolean;
}

interface ObserveRunProgressOutcome {
  /** 最終結果まで受け取って保存できたか。 */
  finalized: boolean;
  /** 呼び出し側の中断で降りたか（失敗として扱わない）。 */
  aborted: boolean;
}

const OBSERVE_MAX_RETRIES = 5;
const OBSERVE_RETRY_DELAY_MS = 2_000;

function createEventHandler(
  store: AICheckState,
  saveContactTopics: (projectId: number, result: ContactTopicsResult) => void,
  projectId: number,
  language: OutputLanguage,
  checkpoint: ReturnType<typeof useRunCheckpoint>,
) {
  const getStartedAt = () =>
    useAICheckStore.getState().partialRunData?.startedAt ??
    new Date().toISOString();
  const syncCheckpointProgress = () => {
    const state = useAICheckStore.getState();
    checkpoint.syncRunProgress(projectId, language, getStartedAt(), {
      lastKnownStatus: state.runProgress.status,
      failedStage: state.runProgress.failedStage,
      failedChecks: state.runProgress.failedChecks,
      skippedItems: state.runProgress.skippedItems,
      nodeStates: state.runProgress.nodeStates,
      nodeExecutionDirectives: state.runProgress.nodeExecutionDirectives,
      nodeControlVersion: state.runProgress.nodeControlVersion,
    });
  };

  return async (event: Record<string, unknown>) => {
    if (event.type === "heartbeat") return;
    if (event.type === "run-start") {
      const startedAt =
        typeof event.startedAt === "string"
          ? event.startedAt
          : new Date().toISOString();
      const currentState = useAICheckStore.getState();
      const seededCheckpoint = checkpoint.getCheckpoint(
        projectId,
        language,
      ).checkpoint;
      const seededNodeExecutionState = resolveRunStartNodeExecutionState({
        startedAt,
        currentStartedAt: currentState.partialRunData?.startedAt,
        currentNodeStates: currentState.runProgress.nodeStates,
        currentNodeExecutionDirectives:
          currentState.runProgress.nodeExecutionDirectives,
        currentNodeControlVersion: currentState.runProgress.nodeControlVersion,
        checkpoint: seededCheckpoint,
      });
      store.initPartialRun(projectId, startedAt);
      checkpoint.ensureStarted(projectId, language, startedAt);
      store.setNodeExecutionState(seededNodeExecutionState);
      checkpoint.setNodeExecutionState(projectId, language, startedAt, {
        nodeStates: seededNodeExecutionState.nodeStates,
        nodeExecutionDirectives:
          seededNodeExecutionState.nodeExecutionDirectives,
        nodeControlVersion: seededNodeExecutionState.nodeControlVersion,
      });
    } else if (event.type === "status") {
      store.setApiProgress(undefined);
      store.setRunStatus(event.status as RunStatus, event.step as string);
      checkpoint.setRunStatus(
        projectId,
        language,
        getStartedAt(),
        event.status as RunStatus,
      );
      if (event.totalChecks != null) {
        store.setProgress(
          (event.completedChecks as number) ?? 0,
          event.totalChecks as number,
        );
      }
    } else if (event.type === "check-complete") {
      store.removeActiveCheck(event.checkName as string);
      const completedCheckIds = Array.isArray(event.checkIds)
        ? (event.checkIds as string[])
        : [];
      if (completedCheckIds.length > 0) {
        store.markCompletedByCheckIds(completedCheckIds);
      }
      store.setProgress(
        event.completedChecks as number,
        event.totalChecks as number,
      );
      store.setRunStatus("running-ai", `Running: ${event.checkName as string}`);
      checkpoint.setRunStatus(
        projectId,
        language,
        getStartedAt(),
        "running-ai",
      );
    } else if (event.type === "remove-active-check") {
      store.removeActiveCheck(event.checkName as string);
    } else if (event.type === "check-gist") {
      // ⑤ ライブ実況: チェック完了ごとの1行「速報」を実況ログへ積む（表示専用）。
      const checkName =
        typeof event.checkName === "string" ? event.checkName : "";
      const gist = typeof event.gist === "string" ? event.gist : "";
      if (gist) {
        store.appendLiveLog(checkName ? `${checkName}: ${gist}` : gist);
      }
    } else if (event.type === "graph-skeleton") {
      const graphSkeleton = event.data as RunProgress["graphSkeleton"];
      store.setGraphSkeleton(graphSkeleton);
      if (graphSkeleton) {
        checkpoint.setGraphSkeleton(
          projectId,
          language,
          getStartedAt(),
          graphSkeleton,
        );
      }
    } else if (event.type === "node-members") {
      const nodeMembers = event.data as PipelineNodeMembers;
      store.setNodeMembers(nodeMembers);
      checkpoint.setNodeMembers(
        projectId,
        language,
        getStartedAt(),
        nodeMembers,
      );
    } else if (event.type === "planned-checks") {
      if (!useAICheckStore.getState().runProgress.graphSkeleton) {
        throw new Error(
          "Protocol violation: graph-skeleton must arrive before planned-checks",
        );
      }
      const checks = event.checks;
      const checkIds = event.checkIds;
      const lane = event.lane as
        | "independent"
        | "dependent"
        | "afterAll"
        | undefined;
      if (
        Array.isArray(checks) &&
        checks.every((c: unknown): c is string => typeof c === "string")
      ) {
        store.addPlannedChecks(
          checks,
          lane,
          Array.isArray(checkIds) ? (checkIds as string[]) : undefined,
        );
        checkpoint.addPlannedChecks(
          projectId,
          language,
          getStartedAt(),
          checks,
          lane,
          Array.isArray(checkIds) ? (checkIds as string[]) : undefined,
        );
      }
      if (Array.isArray(checkIds) && !Array.isArray(checks)) {
        store.addPlannedCheckIds(checkIds as string[]);
      }
    } else if (event.type === "api-progress") {
      store.setApiProgress({
        checkName: event.checkName as string,
        batchGroupId:
          typeof event.batchGroupId === "string"
            ? (event.batchGroupId as string)
            : undefined,
        phase: event.phase as "waiting" | "streaming",
        tokensReceived: event.tokensReceived as number | undefined,
        bytesReceived: event.bytesReceived as number | undefined,
        contentLength: event.contentLength as number | undefined,
        attempt: event.attempt as number | undefined,
      });
    } else if (event.type === "llm-output-oversize") {
      // **コンソールに出す。** 出力が上限で切られたとき、何を延々と生成して
      // いたのかは本文を見ないと分からない。サーバのログにも出しているが、
      // 画面を見ている人がその場で判断できるようにここにも出す。
      // group にするのは、本文の抜粋が長く既定の1行表示では読めないため。
      console.group(
        `[aicheck] LLM output hit the token limit — ${
          (event.batchGroupName as string | undefined) ??
          (event.checkIds as string[] | undefined)?.join(", ") ??
          "unknown batch"
        }`,
      );
      const completion = event.completionTokens as number | undefined;
      const limit = event.maxTokensLimit as number | undefined;
      console.error(
        `${
          completion != null && limit != null
            ? `completion ${completion} / limit ${limit} tokens`
            : "stopped before the token usage was known"
        } (estimated ${
          (event.estimatedTokens as number | undefined) ?? "?"
        }), ${event.contentLength as number} chars`,
      );
      console.error("checkIds:", event.checkIds);
      console.error("head of output:", event.head);
      console.error("tail of output:", event.tail);
      console.groupEnd();
    } else if (event.type === "project-data") {
      const projectData = event.data as AggregatedProjectData;
      const projectDataSource = event.source as
        | ProjectDataSourceDescriptor
        | undefined;
      store.setPartialProjectData(projectData, projectDataSource);
      checkpoint.setProjectData(
        projectId,
        language,
        getStartedAt(),
        projectData,
        projectDataSource,
        typeof event.signature === "string" ? event.signature : undefined,
        typeof event.signatureKid === "string" ? event.signatureKid : undefined,
      );
    } else if (event.type === "check-result") {
      const cr = event.data as AICheckResult;
      store.addPartialCheckResult(cr);
      checkpoint.appendAIResult(
        projectId,
        language,
        getStartedAt(),
        cr,
        typeof event.signature === "string" ? event.signature : undefined,
        typeof event.signatureKid === "string" ? event.signatureKid : undefined,
      );
      if (cr.status === "error") {
        const failedNodeId = cr.batchGroupId ?? cr.checkId;
        store.addFailedCheck({
          checkId: failedNodeId,
          checkName: cr.batchGroupName ?? cr.checkName,
          batchGroupId: cr.batchGroupId,
          batchGroupName: cr.batchGroupName,
          rerunCheckIds: [cr.checkId],
          error: cr.error ?? "Unknown",
        });
        if (!store.runProgress.failedStage) {
          store.setFailedStage("ai-checks");
        }
      }
      syncCheckpointProgress();
    } else if (event.type === "profile-result") {
      const pr = event.data as CustomerProfileResult;
      store.setPartialProfileResult(pr);
      checkpoint.setProfileResult(
        projectId,
        language,
        getStartedAt(),
        pr,
        typeof event.signature === "string" ? event.signature : undefined,
        typeof event.signatureKid === "string" ? event.signatureKid : undefined,
      );
      if (pr.error && !pr.profile) {
        store.setFailedStage("profile", pr.error);
      }
      syncCheckpointProgress();
    } else if (event.type === "contact-topics-result") {
      const ctr = event.data as ContactTopicsResult;
      store.setPartialContactTopicsResult(ctr);
      checkpoint.setContactTopicsResult(
        projectId,
        language,
        getStartedAt(),
        ctr,
        typeof event.signature === "string" ? event.signature : undefined,
        typeof event.signatureKid === "string" ? event.signatureKid : undefined,
      );
      if (!ctr.error && !ctr.compactionDegraded) {
        saveContactTopics(projectId, ctr);
      }
      if (ctr.error || ctr.compactionDegraded) {
        store.setFailedStage("compaction", ctr.error ?? "Compaction degraded");
      }
      syncCheckpointProgress();
    } else if (event.type === "consistency-results") {
      const results = event.data as ConsistencyCheckResult[];
      store.addPartialConsistencyResults(results);
      checkpoint.mergeConsistencyResults(
        projectId,
        language,
        getStartedAt(),
        results,
      );
    } else if (event.type === "batch-groups") {
      const groups = event.groups as Array<{
        id: string;
        name: string;
        checks: string[];
      }>;
      store.setCheckBatchGroups(groups);
      checkpoint.setCheckBatchGroups(
        projectId,
        language,
        getStartedAt(),
        groups,
      );
    } else if (event.type === "profile-consistency-results") {
      const results = event.data as ConsistencyCheckResult[];
      store.addProfileConsistencyResults(results);
      checkpoint.mergeConsistencyResults(
        projectId,
        language,
        getStartedAt(),
        results,
      );
    } else if (event.type === "skipped-checks") {
      const items = (event.items as SkippedItem[]) ?? [];
      store.setSkippedItems(items);
      checkpoint.setSkippedItems(projectId, language, getStartedAt(), items);
      syncCheckpointProgress();
    } else if (event.type === "resume-warning") {
      const droppedIds = Array.isArray(event.droppedCheckpointCheckIds)
        ? (event.droppedCheckpointCheckIds as string[])
        : [];
      if (droppedIds.length > 0) {
        const message = `Saved checkpoint dropped ${droppedIds.length} check result(s) during resume: ${droppedIds.join(", ")}`;
        console.warn(`[useAICheckRun] ${message}`);
        if (typeof window !== "undefined") {
          window.setTimeout(() => {
            window.alert(message);
          }, 0);
        }
      }
    } else if (event.type === "node-state") {
      // **ノードの実行状態は流れてくる分で更新する。** `/run/state` は観測を
      // 始めた瞬間の一枚しか返さないので、それだけに頼ると実行中に完了した
      // ノードが永久にグレーのままになる（リロードすると直る、という形で
      // 表に出ていた）。sys-fetch / sys-compaction / sys-profile は専用の
      // 進捗イベントを持たないため、この経路が唯一の更新手段になる。
      const nodeId = typeof event.nodeId === "string" ? event.nodeId : null;
      const state =
        typeof event.state === "string"
          ? (event.state as PipelineNodeRuntimeStatus)
          : null;
      if (nodeId && state) {
        store.mergeNodeStates({ [nodeId]: state });
        checkpoint.setNodeExecutionState(projectId, language, getStartedAt(), {
          nodeStates: useAICheckStore.getState().runProgress.nodeStates,
        });
      }
    } else if (event.type === "run-aborted") {
      const keyframe = parseNodeStateKeyframe(event);
      store.setNodeExecutionState(keyframe);
      const { nodeExecutionDirectives, nodeControlVersion } = keyframe;
      // **「実行中」のまま残さない。** 中断は「もう何も走っていない」という
      // 権威ある事実なので、ノード単位の状態が届かなかった場合でも、実行中
      // として持っているノードは終端させる。ここで作るのは新しい事実では
      // なく、届いた事実の帰結（走っていない ＝ 実行中ではない）。
      const stillActive = Object.entries(
        useAICheckStore.getState().runProgress.nodeStates ?? {},
      ).filter(([, state]) => state === "active");
      if (stillActive.length > 0) {
        store.mergeNodeStates(
          Object.fromEntries(
            stillActive.map(([nodeId]) => [
              nodeId,
              "aborted" as PipelineNodeRuntimeStatus,
            ]),
          ),
        );
      }
      checkpoint.setNodeExecutionState(projectId, language, getStartedAt(), {
        nodeStates: useAICheckStore.getState().runProgress.nodeStates,
        nodeExecutionDirectives,
        nodeControlVersion,
      });
      store.setAborted();
    } else if (event.type === "result") {
      store.setApiProgress(undefined);
      // result handled by the caller
    } else if (event.type === "error") {
      throw new Error(event.error as string);
    } else {
      console.warn("[useAICheckRun] Unknown event type:", event.type);
    }

    // Yield to browser so intermediate states get painted
    await new Promise<void>((r) => setTimeout(r, 0));
  };
}

// ─── Network disconnect detection ────────────────────────────────────

function isNetworkDisconnect(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (
    err instanceof Error &&
    err.message === "No result received from server"
  ) {
    return true;
  }
  return false;
}

function isCacheIntegrityVerificationFailure(
  status: number,
  message: string,
): boolean {
  return (
    status === 403 &&
    message.toLowerCase().includes("cache integrity verification failed")
  );
}

async function parseConflictResponse(
  response: Response,
): Promise<RunConflictResponse | null> {
  if (response.status !== 409) return null;
  const body = (await response
    .json()
    .catch(() => null)) as RunConflictResponse | null;
  if (!body || typeof body.reason !== "string") {
    return {
      ok: false,
      reason: "unknown_conflict",
      message: `Server conflict (${response.status})`,
    };
  }
  return body;
}

function assertServerResultMetadata(
  result: AICheckRunResult,
  context: string,
): void {
  if (result.status !== "completed") return;

  if (!result.graphSkeleton) {
    throw new Error(
      `Protocol violation: missing graph-skeleton in server result (${context})`,
    );
  }
  if (!result.checkLaneMap) {
    throw new Error(
      `Protocol violation: missing check-lane-map in server result (${context})`,
    );
  }
  if (!result.plannedChecks) {
    throw new Error(
      `Protocol violation: missing planned-checks in server result (${context})`,
    );
  }
  if (!result.checkBatchGroups) {
    throw new Error(
      `Protocol violation: missing check-batch-groups in server result (${context})`,
    );
  }
  if (!result.independentConsistencyRules) {
    throw new Error(
      `Protocol violation: missing independent-consistency-rules in server result (${context})`,
    );
  }
  if (!result.profileConsistencyRules) {
    throw new Error(
      `Protocol violation: missing profile-consistency-rules in server result (${context})`,
    );
  }
}

function hasExecutionFailures(result: AICheckRunResult): boolean {
  const summary = deriveRunFailureSummary(result);
  return !!summary.failedStage;
}

class RunCacheNotFoundError extends Error {
  readonly runId: string;
  readonly checkId?: string;

  constructor(message: string, runId: string, checkId?: string) {
    super(message);
    this.name = "RunCacheNotFoundError";
    this.runId = runId;
    this.checkId = checkId;
  }
}

async function fetchRunResultById(
  runId: string,
  token: string,
): Promise<AICheckRunResult> {
  const res = await fetch(
    apiUrl(`/api/aicheck/run/result/${encodeURIComponent(runId)}`),
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) {
    if (res.status === 404) {
      throw new RunCacheNotFoundError("Run result not found", runId);
    }
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error ?? `Server error: ${res.status}`);
  }
  const body = (await res.json()) as { data: AICheckRunResult };
  return body.data;
}

async function fetchCheckResultById(
  runId: string,
  checkId: string,
  token: string,
): Promise<AICheckResult> {
  const res = await fetch(
    apiUrl(
      `/api/aicheck/run/result/${encodeURIComponent(runId)}/checks/${encodeURIComponent(checkId)}`,
    ),
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) {
    if (res.status === 404) {
      throw new RunCacheNotFoundError("Check result not found", runId, checkId);
    }
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error ?? `Server error: ${res.status}`);
  }
  const body = (await res.json()) as { data: AICheckResult };
  return body.data;
}

interface ServerRunStateResponse {
  status: "in_flight" | "completed" | "not_found";
  runId: string | null;
  nodeStates: Record<string, PipelineNodeRuntimeStatus>;
  nodeExecutionDirectives: NodeExecutionDirective[];
  nodeControlVersion: number;
}

interface ConsumedRunStream {
  result: AICheckRunResult | null;
  runId: string | null;
  aborted?: boolean;
}

async function fetchServerRunState(
  projectId: number,
  language: OutputLanguage,
  token: string,
): Promise<ServerRunStateResponse> {
  const res = await fetch(
    apiUrl(
      `/api/aicheck/run/state?projectId=${encodeURIComponent(String(projectId))}&language=${encodeURIComponent(language)}`,
    ),
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error ?? `Server error: ${res.status}`);
  }
  return (await res.json()) as ServerRunStateResponse;
}

async function releaseServerRunResult(
  runId: string,
  token: string,
): Promise<void> {
  if (!runId) return;
  try {
    await fetch(
      apiUrl(`/api/aicheck/run/result/${encodeURIComponent(runId)}/release`),
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  } catch {
    // Best effort only
  }
}

async function consumeRunStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  projectId: number,
  language: OutputLanguage,
  token: string,
  handleEvent: (event: Record<string, unknown>) => Promise<void>,
  /**
   * 生のイベントを 1 件残らず渡す。**分岐で continue される前に呼ぶ。**
   * 観測経路はここで通し番号を追っており、取りこぼすと繋ぎ直しのときに
   * そのイベント（と付随する取得）をもう一度処理してしまう。
   */
  onRawEvent?: (event: Record<string, unknown>) => void,
): Promise<ConsumedRunStream> {
  let result: AICheckRunResult | null = null;
  let streamRunId: string | null = null;

  for await (const event of readNDJSONStream<Record<string, unknown>>(reader)) {
    onRawEvent?.(event);
    if (event.type === "run-start") {
      streamRunId = String(event.runId ?? "");
      await handleEvent(event);
      continue;
    }
    if (event.type === "result") {
      result = event.data as AICheckRunResult;
      continue;
    }
    if (event.type === "result-ready") {
      const runId = String(event.runId ?? "");
      streamRunId = runId || streamRunId;
      try {
        result = await fetchRunResultById(runId, token);
      } catch (err) {
        if (!(err instanceof RunCacheNotFoundError)) throw err;
        const state = await fetchServerRunState(projectId, language, token);
        if (state.status === "completed" && state.runId) {
          streamRunId = state.runId;
          result = await fetchRunResultById(state.runId, token);
        } else {
          throw new Error(
            "Run result cache was not found. Please retry or reload the project.",
          );
        }
      }
      continue;
    }
    if (event.type === "check-result-ready") {
      const runId = String(event.runId ?? streamRunId ?? "");
      const checkId = String(event.checkId ?? "");
      if (runId && checkId) {
        try {
          const checkResult = await fetchCheckResultById(runId, checkId, token);
          await handleEvent({
            type: "check-result",
            data: checkResult,
            signature:
              typeof event.signature === "string" ? event.signature : undefined,
            signatureKid:
              typeof event.signatureKid === "string"
                ? event.signatureKid
                : undefined,
          });
        } catch (err) {
          if (!(err instanceof RunCacheNotFoundError)) throw err;
          const state = await fetchServerRunState(projectId, language, token);
          if (state.status === "completed" && state.runId) {
            const recovered = await fetchRunResultById(state.runId, token);
            const recoveredCheck = recovered.aiResults.find(
              (r) => r.checkId === checkId,
            );
            if (recoveredCheck) {
              await handleEvent({
                type: "check-result",
                data: recoveredCheck,
              });
            }
          }
        }
      }
      continue;
    }
    if (event.type === "run-aborted") {
      await handleEvent(event);
      return { result: null, runId: streamRunId, aborted: true };
    }
    await handleEvent(event);
  }

  return { result, runId: streamRunId };
}

// ─── Shared post-stream finalization ─────────────────────────────────
//
// consumeRunStream() を消費し終えた 4 経路（full run / selected rerun /
// reconnect / checkpoint resume）の後処理を 1 箇所に集約する。経路固有の
// 差分はすべて opts で明示的に渡す。
//
//  - assertLabel: 未指定なら assert をスキップ（selected rerun は helper 側で
//    既に assert 済みのため未指定で呼ぶ）。
//  - runId: null なら release をスキップ（selected rerun は helper 側で既に
//    release 済みのため null で呼ぶ）。それ以外は完了・成功時のみ release。
//  - projectDataComparison: withProjectDataComparison に渡す比較スナップショット。
//  - mergeCheckCache: 指定時のみ merge-check キャッシュへ書き込む（full run のみ）。
//  - saveContactTopics: true のとき、劣化・エラーの無い contactTopics を保存する
//    （full run / checkpoint resume のみ。reconnect / selected rerun は保存しない）。
interface FinalizeRunStreamDeps {
  projectId: number;
  language: OutputLanguage;
  // release を行う経路でのみ必須。selected rerun は helper 側で release 済みの
  // ため token を持たず（runId:null で呼ぶ）、ここは省略される。
  token?: string;
  store: AICheckState;
  checkpoint: ReturnType<typeof useRunCheckpoint>;
  saveResult: (result: AICheckRunResult) => void;
  saveContactTopics: (projectId: number, result: ContactTopicsResult) => void;
}

interface FinalizeRunStreamOptions {
  assertLabel?: string;
  runId: string | null;
  projectDataComparison?: ProjectDataComparisonSnapshot | null;
  mergeCheckCache?: MergeCheckCacheMetadata;
  saveContactTopics?: boolean;
}

export async function finalizeRunStreamResult(
  result: AICheckRunResult,
  deps: FinalizeRunStreamDeps,
  opts: FinalizeRunStreamOptions,
): Promise<AICheckRunResult> {
  if (opts.assertLabel) {
    assertServerResultMetadata(result, opts.assertLabel);
  }
  const cacheableResult = withProjectDataComparison(
    result,
    opts.projectDataComparison ?? undefined,
  );
  deps.saveResult(cacheableResult);
  if (opts.mergeCheckCache) {
    writeMergeCheckResultCache({
      metadata: opts.mergeCheckCache,
      result: cacheableResult,
    });
  }
  deps.store.completeRun(cacheableResult);
  deps.checkpoint.clearCheckpoint(deps.projectId, deps.language);
  if (
    opts.saveContactTopics &&
    cacheableResult.contactTopicsResult &&
    !cacheableResult.contactTopicsResult.compactionDegraded &&
    !cacheableResult.contactTopicsResult.error
  ) {
    deps.saveContactTopics(deps.projectId, cacheableResult.contactTopicsResult);
  }
  if (
    opts.runId &&
    deps.token &&
    cacheableResult.status === "completed" &&
    !hasExecutionFailures(cacheableResult)
  ) {
    await releaseServerRunResult(opts.runId, deps.token);
  }
  return cacheableResult;
}

function isResumeReadyCheckpoint(checkpoint: AICheckRunCheckpoint): boolean {
  return Boolean(
    checkpoint.projectDataSnapshot &&
      checkpoint.plannedCheckIds &&
      checkpoint.plannedCheckNameById &&
      checkpoint.checkLaneMap &&
      checkpoint.checkBatchGroups &&
      checkpoint.graphSkeleton,
  );
}

function buildCheckpointResumeRequest(
  projectId: number,
  language: OutputLanguage,
  checkpoint: AICheckRunCheckpoint,
  remainingCheckIds: string[],
): {
  projectId: number;
  language: OutputLanguage;
  checkIds: string[];
  resumeFromCheckpoint: true;
  reevaluateConsistency: true;
  checkpointSignatureKid?: string;
  checkpointProjectData: CheckpointSignedValue<AggregatedProjectData>;
  checkpointAIResults: CheckpointAIResultEntry[];
  checkpointCustomerProfileResult?: CheckpointSignedValue<CustomerProfileResult | null>;
  checkpointContactTopicsResult?: CheckpointSignedValue<ContactTopicsResult | null>;
} {
  return {
    projectId,
    language,
    checkIds: remainingCheckIds,
    resumeFromCheckpoint: true,
    reevaluateConsistency: true,
    checkpointSignatureKid: checkpoint.signatureKid,
    checkpointProjectData: {
      data: checkpoint.projectDataSnapshot as AggregatedProjectData,
      signature: checkpoint.projectDataSignature,
    },
    checkpointAIResults: checkpoint.aiResults ?? [],
    ...(checkpoint.customerProfileResult
      ? { checkpointCustomerProfileResult: checkpoint.customerProfileResult }
      : undefined),
    ...(checkpoint.contactTopicsResult
      ? { checkpointContactTopicsResult: checkpoint.contactTopicsResult }
      : undefined),
  };
}

// ─── Selected-checks request body ────────────────────────────────────
//
// 選択チェックの再実行本文。`POST /api/aicheck/runs` のパーサ
// （run-request.ts）が旧経路と同じ本文を受けるので、そのまま送れる。
// `useCachedPayload = false` はキャッシュ整合性エラー後の再試行用で、
// 署名付きの持ち込みを一切載せない。
function buildSelectedChecksRequest(
  projectId: number,
  language: OutputLanguage,
  checkIds: string[],
  dismissedCheckIds: string[] | undefined,
  options: {
    skipProfile?: boolean;
    skipCompaction?: boolean;
    reevaluateConsistency?: boolean;
  },
  cachedResult: AICheckRunResult | null,
): { body: Record<string, unknown>; reusesCachedProjectData: boolean } {
  const hasCachedResult =
    !!cachedResult &&
    cachedResult.projectId === projectId &&
    cachedResult.language === language;
  const signedPreviousContactTopics =
    hasCachedResult &&
    cachedResult.contactTopicsResult &&
    cachedResult._signatures?.contactTopics
      ? cachedResult.contactTopicsResult
      : null;
  const reusesCachedProjectData = Boolean(
    hasCachedResult &&
      cachedResult.projectData &&
      cachedResult.projectDataSource &&
      (cachedResult.projectDataSource.kind !== "crm-api" ||
        cachedResult.projectData.mergeCheckContext),
  );

  return {
    reusesCachedProjectData,
    body: {
      projectId,
      language,
      checkIds,
      ...(reusesCachedProjectData && cachedResult
        ? {
            mode: "single-project-data",
            projectData: cachedResult.projectData,
            projectDataSource: cachedResult.projectDataSource,
          }
        : undefined),
      ...(signedPreviousContactTopics
        ? { previousContactTopics: signedPreviousContactTopics }
        : undefined),
      ...(dismissedCheckIds?.length ? { dismissedCheckIds } : undefined),
      skipCompaction: options.skipCompaction ?? true,
      reevaluateConsistency: options.reevaluateConsistency ?? false,
      ...(hasCachedResult && cachedResult
        ? {
            skipProfile: options.skipProfile ?? true,
            previousAIResults: cachedResult.aiResults,
            previousConsistencyResults: cachedResult.consistencyResults,
            previousCustomerProfileResult: cachedResult.customerProfileResult,
            _signatures: cachedResult._signatures,
          }
        : undefined),
    },
  };
}

// ─── Main hook ───────────────────────────────────────────────────────

export function useAICheckRun() {
  const store = useAICheckStore();
  const checkpoint = useRunCheckpoint();
  const {
    saveResult,
    getResult,
    fetchResultFromServer,
    clearAllResults,
    saveContactTopics,
    clearSelectedCaches,
  } = useLocalResults();
  const abortControllerRef = useRef<AbortController | null>(null);
  /**
   * いま画面が相手にしている実行の runId。
   *
   * **停止は runId で指せなければならない。** 旧 `/api/aicheck/run/cancel` は
   * 「案件 + 言語 + 依頼者の JWT」から組んだ scopeKey で実行を探すが、
   * `POST /api/aicheck/runs` で始まった実行は案件単位のスコープに登録される
   * ため、その経路では届かない。届かないと画面上は止まったように見えて
   * サーバ側は走り続け、次の実行がその「止めたはずの run」に相乗りする。
   *
   * 旧経路で始めた実行（選択実行・チェックポイント再開）には runId が
   * 無いので、その場合だけ従来の scopeKey 経由で止める。
   */
  const activeRunIdRef = useRef<string | null>(null);

  const stopRunOnServer = useCallback(
    (projectId: number, language: OutputLanguage) => {
      void (async () => {
        try {
          const token = await authService.getIdToken();
          if (!token) return;

          // **runId を覚えていなければサーバに聞く。** 旧
          // `/api/aicheck/run/cancel` は依頼者の JWT から組んだ scopeKey で
          // 実行を探すため、`POST /api/aicheck/runs` の実行（案件単位の
          // スコープ）には**届かない**。届かないと画面上は止まったのに
          // サーバは走り続け、しばらくして完了する（実際にそうなった）。
          //
          // 実行の同一性を持っているのはサーバなので、知らないときは
          // 覚えているかどうかに賭けず、その場で問い合わせる。
          let runId = activeRunIdRef.current;
          if (!runId) {
            try {
              const state = await fetchServerRunState(
                projectId,
                language,
                token,
              );
              if (state.status === "in_flight" && state.runId) {
                runId = state.runId;
              } else {
                // **止める相手が居ない。** サーバが「実行なし」と答えている
                // のだから、旧経路を叩いても 404 になるだけ。失敗リクエストを
                // 残すと、本当の不具合を追うときのノイズになる。
                return;
              }
            } catch {
              // 問い合わせ自体に失敗した場合だけ旧経路へ落とす
              // （状態が分からないので、止める試み自体は残す）
            }
          }

          if (runId) {
            await fetch(
              apiUrl(`/api/aicheck/runs/${encodeURIComponent(runId)}/cancel`),
              { method: "POST", headers: { Authorization: `Bearer ${token}` } },
            );
            return;
          }
          // runId が分からない = 旧経路で始まった実行。従来どおり scopeKey で止める。
          await fetch(apiUrl("/api/aicheck/run/cancel"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ projectId, language }),
          });
        } catch {
          // Best-effort: server may have already finished.
        }
      })();
    },
    [],
  );

  /**
   * 停止を頼んだあと、自分の観測を畳むまでの猶予。
   *
   * **止めた本人こそ、止まった姿を受け取れなければならない。** 以前はここで
   * 即座に自分のストリームを切っていたため、サーバがそのあと配る中断状態
   * （実行中だったノードの aborted）が押した本人にだけ届かず、グラフは
   * オレンジのまま点滅し続けた。同じ実行を別のブラウザで開くと正しく
   * 中断済みで見えていた — 観測を切っていたのが自分だけだったから。
   *
   * 終端イベントが来ればストリームはそこで閉じるので、この猶予は
   * 「サーバが応えなかったとき接続を抱えたままにしない」ための保険。
   *
   * **サーバが配信に使う猶予より長くする。** サーバは観測者 1 人あたり
   * 20 秒（runner.ts の LISTENER_TIMEOUT_MS）まで配信を待つので、それより
   * 短くすると、負荷が高いときだけ「配信の途中で自分が切る」= 直したはずの
   * 症状が再発する。定数を直接 import しないのはサーバ専用モジュールを
   * クライアントへ引き込まないため。
   */
  const ABORT_OBSERVE_GRACE_MS = 30_000;

  const detachObservationAfterGrace = useCallback(
    (controller: AbortController) => {
      window.setTimeout(() => {
        controller.abort();
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }, ABORT_OBSERVE_GRACE_MS);
    },
    [],
  );

  /**
   * 停止をサーバへ頼み、観測は畳まずに残す。
   *
   * 「中断」と「取り消し」の違いは、表示に出す状態と、チェックポイントを
   * 捨てるかどうかだけ。観測の扱いは同じでなければならない（片方だけ直す
   * と、もう片方でオレンジが点滅したまま残る）。
   */
  const requestStop = useCallback(
    (projectId: number, language: OutputLanguage, markStopped: () => void) => {
      const observing = abortControllerRef.current;
      markStopped();
      stopRunOnServer(projectId, language);
      // 観測はここで切らない（上のコメント参照）。終端イベントを受け取って
      // からストリーム側が閉じる。
      if (observing) detachObservationAfterGrace(observing);
    },
    [stopRunOnServer, detachObservationAfterGrace],
  );

  const abortRun = useCallback(
    (projectId: number, language: OutputLanguage) => {
      requestStop(projectId, language, () => store.setAborted());
    },
    [store, requestStop],
  );

  const cancelRun = useCallback(
    (projectId: number, language: OutputLanguage) => {
      requestStop(projectId, language, () => {
        store.setCancelled();
        checkpoint.clearCheckpoint(projectId, language);
      });
    },
    [store, checkpoint, requestStop],
  );

  const runCheck = useCallback(
    async (
      projectId: number,
      language: OutputLanguage,
      options?: RunCheckOptions,
    ) => {
      abortControllerRef.current?.abort();
      // 前の実行の runId で止めてしまわないよう、開始が確定するまで空にする
      activeRunIdRef.current = null;
      const externalProjectDataMode = options?.mode === "single-project-data";

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const hasResultForSameProject =
        (store.currentResult &&
          store.currentResult.projectId === projectId &&
          store.currentResult.language === language) ||
        (store.previousResult &&
          store.previousResult.projectId === projectId &&
          store.previousResult.language === language);
      if (hasResultForSameProject) {
        store.startRerun();
      } else {
        store.reset();
      }
      store.setStartedAt(Date.now());
      store.setRunStatus("fetching-data", "Fetching project data...");
      store.initPartialRun(projectId, new Date().toISOString());

      // Persist run language for reconnect
      try {
        sessionStorage.setItem("aicheck:runLanguage", language);
      } catch {
        // sessionStorage may be unavailable in some environments
      }

      // 進捗イベントの解釈は observeRunProgress 側が持つ（開始した本人も
      // 途中から見に来た人も同じ経路を通す）。
      const saveRunResultCaches = (result: AICheckRunResult) => {
        const cacheableResult = withProjectDataComparison(
          result,
          options?.projectDataComparison,
        );
        saveResult(cacheableResult);
        if (options?.mergeCheckCache) {
          writeMergeCheckResultCache({
            metadata: options.mergeCheckCache,
            result: cacheableResult,
          });
        }
        return cacheableResult;
      };

      try {
        const token = await authService.getIdToken();
        if (!token) {
          store.setError("Not authenticated");
          return;
        }
        const { runId: startedRunId, started } = await startRunRequest({
          token,
          signal: controller.signal,
          build: (useCachedPayload) => {
            // 署名付きの contactTopics を持っているものだけを持ち込む。
            // 外部データ投入では CRM 由来のキャッシュを混ぜない。
            const hasSignedContactTopics = (
              candidate: AICheckRunResult | null | undefined,
            ) =>
              !!candidate &&
              candidate.projectId === projectId &&
              candidate.language === language &&
              !!candidate.contactTopicsResult &&
              !!candidate._signatures?.contactTopics;
            // 保存済みキャッシュの読み出しは、メモリ上に見つからなかったとき
            // だけ行う（大きな結果の JSON.parse を毎回走らせない）。
            const findSignedSource = (): AICheckRunResult | null => {
              if (!useCachedPayload || externalProjectDataMode) return null;
              for (const read of [
                () => store.currentResult,
                () => store.previousResult,
                () => getResult(projectId, language).result,
              ]) {
                const candidate = read();
                if (hasSignedContactTopics(candidate)) return candidate;
              }
              return null;
            };
            const signedSource = findSignedSource();

            return {
              body: {
                projectId,
                language,
                ...(externalProjectDataMode
                  ? {
                      mode: "single-project-data",
                      ...(options?.conversionId
                        ? { conversionId: options.conversionId }
                        : undefined),
                      ...(options?.projectData
                        ? { projectData: options.projectData }
                        : undefined),
                      ...(options?.projectDataSource
                        ? { projectDataSource: options.projectDataSource }
                        : undefined),
                    }
                  : undefined),
                ...(signedSource?.contactTopicsResult
                  ? { previousContactTopics: signedSource.contactTopicsResult }
                  : undefined),
                ...(signedSource?._signatures
                  ? { _signatures: signedSource._signatures }
                  : undefined),
              },
              meta: null,
            };
          },
          onCacheIntegrityFailure: () => {
            clearAllResults(projectId);
            clearSelectedCaches(projectId, { contactTopics: true });
            window.alert(
              `Cached AI check data for project #${projectId} failed integrity verification and was cleared.\n\nThe check will restart automatically.`,
            );
          },
          failureMessage: "Failed to start AI check",
        });

        checkpoint.clearCheckpoint(projectId, language);
        // 停止をこの実行へ届かせる（案件単位スコープの run は旧 cancel では
        // 探せない）
        activeRunIdRef.current = startedRunId;

        // 実行の観測は「途中から見に来た人」と同じ経路で行う。開始した
        // 本人だけ別扱いにすると、その経路だけが直らないまま残る。
        // 表示状態はこの関数の冒頭で組み直し済みなので resetProgress は
        // 渡さない（渡すと initPartialRun の結果まで消える）。
        const outcome = await observeRunProgressRef.current?.(
          startedRunId,
          // 外部データ投入では、案件 ID はサーバが同一性から導く
          Number(started.projectId) || projectId,
          language,
          controller.signal,
          {
            resetProgress: false,
            announceReconnecting: false,
            projectDataComparison: options?.projectDataComparison,
            mergeCheckCache: options?.mergeCheckCache,
            saveContactTopics: true,
          },
        );

        if (!outcome || outcome.aborted) return;
        if (!outcome.finalized) {
          throw new Error("No result received from server");
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }

        // Network disconnect — recover from server state only
        if (isNetworkDisconnect(err)) {
          const token = await authService.getIdToken();
          const recovered =
            !!token &&
            (await (async () => {
              try {
                const state = await fetchServerRunState(
                  projectId,
                  language,
                  token,
                );
                if (state.status === "in_flight") {
                  store.setNodeExecutionState({
                    nodeStates: authoritativeNodeStates(state.nodeStates),
                    nodeExecutionDirectives: state.nodeExecutionDirectives,
                    nodeControlVersion: state.nodeControlVersion,
                  });
                  store.setDisconnected(
                    "Connection lost while server run is still in progress",
                  );
                  return true;
                }
                if (state.status === "completed" && state.runId) {
                  // 実行は終わっている。以降は保存済み結果の表示に任せる。
                  store.clearNodeExecutionState();
                  const result = await fetchRunResultById(state.runId, token);
                  assertServerResultMetadata(result, "server-state recovery");
                  const cacheableResult = saveRunResultCaches(result);
                  store.completeRun(cacheableResult);
                  checkpoint.clearCheckpoint(projectId, language);
                  return true;
                }
                if (state.status === "not_found") {
                  const savedCheckpoint = checkpoint.getCheckpoint(
                    projectId,
                    language,
                  ).checkpoint;
                  const staleDirectives = toStaleNodeExecutionDirectives(
                    savedCheckpoint?.nodeExecutionDirectives,
                  );
                  if (savedCheckpoint) {
                    applyCheckpointToStore(
                      projectId,
                      language,
                      savedCheckpoint,
                      {
                        status: "disconnected",
                        currentStep: "Connection lost. Resume available.",
                        nodeExecutionDirectives: staleDirectives,
                        clearCurrentResult: true,
                      },
                    );
                    checkpoint.setNodeExecutionState(
                      projectId,
                      language,
                      savedCheckpoint.startedAt,
                      {
                        nodeStates: savedCheckpoint.nodeStates,
                        nodeExecutionDirectives: staleDirectives,
                        nodeControlVersion:
                          savedCheckpoint.nodeControlVersion ?? 0,
                      },
                    );
                  }
                  store.setDisconnected(
                    "Connection lost and no recoverable server run was found",
                  );
                  return true;
                }
              } catch {
                return false;
              }
              return false;
            })());
          if (recovered) return;
          store.setDisconnected("Connection lost while checking server state");
          return;
        }

        let message = getErrorMessage(err, "Unknown error occurred");
        if (message === "No result received from server") {
          message =
            "Connection to server was lost before results were received. This may be due to a timeout. Please try again.";
        }
        store.setError(message);
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [
      store,
      checkpoint,
      saveResult,
      getResult,
      clearAllResults,
      clearSelectedCaches,
    ],
  );

  const fetchProjectData = useCallback(async (projectId: number) => {
    const token = await authService.getIdToken();
    if (!token) throw new Error("Not authenticated");

    const response = await fetch(
      apiUrl(`/api/aicheck/project-data/${projectId}`),
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error ?? `Server error: ${response.status}`);
    }

    return response.json();
  }, []);

  const loadCachedResult = useCallback(
    async (
      projectId: number,
      language: OutputLanguage,
    ): Promise<{ loaded: boolean; staleReason: CacheStaleReason | null }> => {
      const { staleReason } = getResult(projectId, language);
      // Tab-memory first, then server history (the durable copy).
      const cached =
        getResult(projectId, language).result ??
        (await fetchResultFromServer(projectId, language));
      if (cached) {
        if (
          !cached.graphSkeleton ||
          !cached.checkLaneMap ||
          !cached.plannedChecks ||
          !cached.plannedCheckIds ||
          !cached.plannedCheckNameById ||
          !cached.checkBatchGroups ||
          !cached.independentConsistencyRules ||
          !cached.profileConsistencyRules
        ) {
          return { loaded: false, staleReason: "legacy" };
        }
        const {
          plannedChecks,
          checkBatchGroups,
          checkLaneMap,
          completedCheckNames,
          failedChecks,
          skippedItems,
          failedStage,
          stageError,
        } = reconstructProgressFromCache(cached);

        const independentConsistencyRules = cached.independentConsistencyRules;
        const profileConsistencyRules = cached.profileConsistencyRules;

        // Atomic update: set both currentResult and runProgress in a single
        // set() call so there is no intermediate render where currentResult
        // is set but status is still "idle".
        useAICheckStore.setState((state) => ({
          currentResult: cached,
          previousResult: null,
          runProgress: {
            ...state.runProgress,
            status: "completed" as const,
            currentStep: "Loaded from cache",
            error: undefined,
            checkLaneMap,
            plannedChecks,
            completedCheckNames,
            completedChecks: completedCheckNames.length,
            totalChecks: plannedChecks.length,
            checkBatchGroups,
            graphSkeleton: cached.graphSkeleton,
            failedChecks,
            skippedItems,
            failedStage,
            stageError,
            plannedCheckIds: cached.plannedCheckIds,
            plannedCheckNameById: cached.plannedCheckNameById,
            nodeMembers: cached.nodeMembers,
            nodeStates: undefined,
            nodeExecutionDirectives: [],
            nodeControlVersion: 0,
            consistencyRuleResults: independentConsistencyRules,
            profileConsistencyRuleResults: profileConsistencyRules,
          },
        }));
        return { loaded: true, staleReason: null };
      }

      // Cache miss for this language — clear any stale in-memory result
      // so we don't display results from a different language.
      useAICheckStore.setState((state) => {
        const shouldClearCurrent =
          state.currentResult?.projectId === projectId &&
          state.currentResult?.language !== language;
        const shouldClearPrevious =
          state.previousResult?.projectId === projectId &&
          state.previousResult?.language !== language;
        if (!shouldClearCurrent && !shouldClearPrevious) return state;
        return {
          ...state,
          currentResult: shouldClearCurrent ? null : state.currentResult,
          previousResult: shouldClearPrevious ? null : state.previousResult,
        };
      });

      return { loaded: false, staleReason };
    },
    [getResult, fetchResultFromServer],
  );

  const runSelectedChecks = useCallback(
    async (
      projectId: number,
      language: OutputLanguage,
      checkIds: string[],
      dismissedCheckIds?: string[],
      options?: {
        skipProfile?: boolean;
        skipCompaction?: boolean;
        reevaluateConsistency?: boolean;
      },
    ) => {
      abortControllerRef.current?.abort();
      // 開始する前に前の実行の runId を捨てる。残っていると「今の実行を
      // 止めたつもりで前の実行を止める」になる。
      activeRunIdRef.current = null;
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const baseResult = store.currentResult ?? store.previousResult ?? null;
      store.resetForSelectedRerun({
        baseResult,
        checkIds,
        skipProfile: options?.skipProfile,
        skipCompaction: options?.skipCompaction,
      });
      store.setStartedAt(Date.now());
      store.setRunStatus("running-ai", "Re-running selected checks...");
      checkpoint.clearCheckpoint(projectId, language);

      // Persist run language for reconnect
      try {
        sessionStorage.setItem("aicheck:runLanguage", language);
      } catch {
        // sessionStorage may be unavailable in some environments
      }

      try {
        const token = await authService.getIdToken();
        if (!token) {
          store.setError("Not authenticated");
          return;
        }

        const { runId: startedRunId, meta: sentProjectDataSource } =
          await startRunRequest<AICheckRunResult["projectDataSource"] | null>({
            token,
            signal: controller.signal,
            build: (useCachedPayload) => {
              const cachedResult = useCachedPayload
                ? store.currentResult
                : null;
              const { body, reusesCachedProjectData } =
                buildSelectedChecksRequest(
                  projectId,
                  language,
                  checkIds,
                  dismissedCheckIds,
                  options ?? {},
                  cachedResult,
                );
              return {
                body,
                meta: reusesCachedProjectData
                  ? (cachedResult?.projectDataSource ?? null)
                  : null,
              };
            },
            onCacheIntegrityFailure: () => {
              clearAllResults(projectId);
              clearSelectedCaches(projectId, { contactTopics: true });
              window.alert(
                `Cached AI check data for project #${projectId} failed integrity verification and was cleared.\n\nThe selected checks will restart automatically.`,
              );
            },
            failureMessage: "Failed to start selected checks",
          });
        activeRunIdRef.current = startedRunId;

        // **比較スナップショットは開始前に決める。** 観測経路は結果を内部で
        // 保存するため、結果を見てから判断できない。持ち込んだ projectData を
        // そのまま使わせた場合だけ、データ源が同じであることが確定している。
        // キャッシュ整合性エラー後の再試行では持ち込みを落とすので、比較も
        // 引き継がない（誤った差分を出すよりは出さない）。
        const comparisonToCarry =
          sentProjectDataSource &&
          baseResult &&
          isSameProjectDataSource(
            baseResult.projectDataSource,
            sentProjectDataSource,
          )
            ? baseResult.projectDataComparison
            : undefined;

        // 実行の観測は「途中から見に来た人」と同じ経路で行う。表示は
        // resetForSelectedRerun で組み直し済みなので捨てない。
        const outcome = await observeRunProgressRef.current?.(
          startedRunId,
          projectId,
          language,
          controller.signal,
          {
            resetProgress: false,
            announceReconnecting: false,
            projectDataComparison: comparisonToCarry,
            // 選択再実行では contactTopics を保存しない（従来どおり）
            saveContactTopics: false,
          },
        );

        if (!outcome || outcome.aborted) return;
        if (!outcome.finalized) {
          throw new Error("No result received from server");
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        store.setError(getErrorMessage(err, "Unknown error occurred"));
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [store, checkpoint, clearAllResults, clearSelectedCaches],
  );

  // observeRunProgress は下で定義されるため、参照は ref 経由にする
  // （依存配列に直接書くと定義前参照になる）
  const observeRunProgressRef = useRef<
    | ((
        runId: string,
        projectId: number,
        language: OutputLanguage,
        signal?: AbortSignal,
        options?: ObserveRunProgressOptions,
      ) => Promise<ObserveRunProgressOutcome>)
    | null
  >(null);

  const reconnectRun = useCallback(
    async (projectId: number, language: OutputLanguage): Promise<boolean> => {
      abortControllerRef.current?.abort();
      // 旧経路で始める実行は runId を持たない。前の実行の runId が残ると
      // 「今の実行を止めたつもりで前の実行を止める」になる。
      activeRunIdRef.current = null;
      const controller = new AbortController();
      abortControllerRef.current = controller;
      store.setReconnecting();
      // 途中から見に来た場合も、経過時間は「見始めてから」で測る
      // （サーバは run の開始時刻を返さない）。
      store.setStartedAt(Date.now());

      try {
        const token = await authService.getIdToken();
        if (!token) return false;
        const serverState = await fetchServerRunState(
          projectId,
          language,
          token,
        );
        if (serverState.status === "not_found") {
          return false;
        }
        store.setNodeExecutionState({
          nodeStates: authoritativeNodeStates(serverState.nodeStates),
          nodeExecutionDirectives: serverState.nodeExecutionDirectives,
          nodeControlVersion: serverState.nodeControlVersion,
        });

        // **実行中なら runId で観測する。** 案件と言語だけで実行を探すと、
        // POST /api/aicheck/runs や MCP で始まった実行（案件単位のキーで
        // 登録される）に繋がらない。繋がらないままキャッシュ読み込みへ進むと、
        // 完了済みの前回結果でグラフが埋まり「実行中なのに全部完了」に見える。
        if (serverState.status === "in_flight" && serverState.runId) {
          await observeRunProgressRef.current?.(
            serverState.runId,
            projectId,
            language,
            controller.signal,
          );
          return true;
        }

        // **繋ぎ直す相手が居ない。**
        //
        // 実行中でないなら、サーバは既に「実行なし」と答えている。
        //
        // 実行中なのに runId が無いのは、`/run/state` が in_flight を
        // 判定してから runId を引くまでの間に実行が消えた場合だけ
        // （`hasInFlightRun` と `getInFlightRunByScope` は同じ scopeKey を
        // 見る）。この場合も繋ぐ相手は既に居ない。
        //
        // どちらも呼び出し元はキャッシュ読み込みへ進む。
        return false;
      } catch {
        return false;
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [store],
  );

  /**
   * runId を指定して実行を観測する。
   *
   * **実行の同一性は runId が持つ。** 案件と言語から探す経路（`reconnectRun`
   * → `/run/state`）は、実行がどのスコープキーで登録されたかに依存するため、
   * MCP・スナップショット・出発日トリガで始まった実行には届かないことがある。
   * こちらは runId で直接観測するので、誰がどこから始めた実行でも繋がる。
   */
  const observeRunProgress = useCallback(
    async (
      runId: string,
      projectId: number,
      language: OutputLanguage,
      signal?: AbortSignal,
      options?: ObserveRunProgressOptions,
    ): Promise<ObserveRunProgressOutcome> => {
      const handleEvent = createEventHandler(
        store,
        saveContactTopics,
        projectId,
        language,
        checkpoint,
      );

      // **既存の受信パイプラインをそのまま使う。** `check-result-ready` /
      // `result-ready` は本体を持たず ID だけを載せるため、payload の取得は
      // consumeRunStream の中にある。ここで独自にイベントを捌くと、
      // チェック結果も最終結果も読み込まれないまま「実行中」で止まる。
      let fromSeq = 0;

      // **前回 run の表示を必ず捨てる。** initPartialRun が消すのは
      // partialRunData と liveLog だけで、runProgress（各チェックの状態・
      // graphSkeleton・完了数）は前回のまま残る。実行中の run へ途中から
      // 繋いだ場合は誰もそれを消さないため、グラフが「最初から全部緑」に
      // なる。リプレイは seq=0 から来るので、捨ててから組み直せばよい。
      //
      // **ただし同じ run を見続けているなら捨てない。** 既に観測中の run へ
      // もう一本繋がると（画面が二重に観測を張った場合など）、後から来た方が
      // 先に積み上がった状態を実行中に消してしまう。捨てる理由は「前の run の
      // 表示が残る」ことなので、run が変わらないなら捨てる理由も無い。
      const observingSameRun = activeRunIdRef.current === runId;
      // 途中から見に来た実行も runId で停止できるようにしておく
      activeRunIdRef.current = runId;

      if (options?.resetProgress !== false && !observingSameRun) {
        store.startRerun();
      }

      // **シードはストリームが運ぶ。** 以前はここで `/run/state` を叩いて
      // いたが、あの経路は実行中に重くなり（実測 3.28 秒でタイムアウトし、
      // ブラウザには CORS error として現れる）、しかも案件と言語で実行を
      // 引くため、観測している run とは別の run を掴みうる。
      // 観測開始時の `stream-synced` が、その run 自身のノード状態の
      // 全体像を運ぶので、そちらを唯一のシードにする。

      let attempts = 0;
      // 自分で始めた実行の最初の接続では「再接続中」と出さない
      // （まだ一度も切れていない）。
      let announcedReconnecting = options?.announceReconnecting === false;
      let seenRunStartId: string | null = null;

      while (!signal?.aborted) {
        const token = await authService.getIdToken();
        if (!token) return { finalized: false, aborted: false };

        const res = await fetch(
          apiUrl(
            `/api/aicheck/runs/${encodeURIComponent(runId)}/events?fromSeq=${fromSeq}`,
          ),
          { headers: { Authorization: `Bearer ${token}` }, signal },
        );
        // 404 = 実行も墓標も無い。待っても現れない。
        if (res.status === 404 || !res.ok || !res.body) {
          return { finalized: false, aborted: false };
        }

        const reader = res.body.getReader();
        let consumed: Awaited<ReturnType<typeof consumeRunStream>>;
        try {
          consumed = await consumeRunStream(
            reader,
            projectId,
            language,
            token,
            async (event) => {
              if (
                event.type === "stream-start" ||
                event.type === "stream-synced"
              ) {
                // 観測経路の制御イベント。実行の状態ではないのでストアには
                // 渡さない（渡すと unknown event として警告になる）。
                //
                // ただし stream-synced はノード状態の**全体像**を運ぶ。
                // 変更ログだけに頼ると、リプレイ枠から溢れた分や繋ぎ直しの
                // 隙間で落ちた分を取り戻せない。ここで一度取り込むことで
                // 取りこぼしが自己修復する。
                //
                // **空のキーフレームは適用しない。** サーバが実行状態を
                // 持っていないとき（run-start 前・TTL 失効・完了後の掃除）
                // は「情報が無い」であって「何も走っていない」ではない。
                // 適用すると、リプレイで組み直した状態を消したうえで、
                // 直後に届く後段ノードの node-state だけが残り、部分的な
                // マップが全体像として扱われる。他に情報源の無い
                // sys-fetch / sys-compaction / sys-profile はそのまま
                // 灰色で残る（実際にそう見えていた）。
                //
                // 適用してよい値の判断は parseNodeStateKeyframe が一つだけ
                // 持つ（空・不在の規則は authoritativeNodeStates に集約）。
                if (event.type === "stream-synced") {
                  store.setNodeExecutionState(parseNodeStateKeyframe(event));
                }
                return;
              }
              // 同じ run の run-start をリプレイで二度受けると
              // initPartialRun が走り、積み上げた途中結果が消える。
              if (event.type === "run-start") {
                const id = String(event.runId ?? "");
                if (seenRunStartId === id) return;
                seenRunStartId = id;
              }
              if (
                event.type === "result" ||
                event.type === "result-ready" ||
                event.type === "check-result-ready"
              ) {
                store.setApiProgress(undefined);
              }
              await handleEvent(event);
            },
            (raw) => {
              // **全イベントで進める。** result-ready / check-result-ready は
              // 上のコールバックに来る前に consumeRunStream 内で continue
              // されるため、ここで拾わないと繋ぎ直しで二重取得になる。
              if (typeof raw.seq === "number") fromSeq = raw.seq + 1;
              if (raw.type === "stream-start" && !announcedReconnecting) {
                announcedReconnecting = true;
                // 完了済み run のリプレイなら「再接続中」とは出さない
                if (raw.finished !== true) store.setReconnecting();
              }
            },
          );
        } finally {
          // 途中で抜けても接続を掴んだままにしない
          void reader.cancel().catch(() => {});
        }
        const { result, aborted, runId: streamRunId } = consumed;

        if (aborted || signal?.aborted) {
          return { finalized: false, aborted: true };
        }
        if (result) {
          await finalizeRunStreamResult(
            result,
            {
              projectId,
              language,
              token,
              store,
              checkpoint,
              saveResult,
              saveContactTopics,
            },
            {
              assertLabel: "observe finalization",
              runId: streamRunId ?? runId,
              projectDataComparison: options?.projectDataComparison,
              mergeCheckCache: options?.mergeCheckCache,
              saveContactTopics: options?.saveContactTopics,
            },
          );
          return { finalized: true, aborted: false };
        }

        // 結果を見ないまま切れた。前進が無いまま繰り返すなら諦める。
        attempts += 1;
        if (attempts > OBSERVE_MAX_RETRIES) {
          return { finalized: false, aborted: false };
        }
        // 待っている間に中断されたら即座に抜ける
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, OBSERVE_RETRY_DELAY_MS);
          signal?.addEventListener(
            "abort",
            () => {
              clearTimeout(timer);
              resolve();
            },
            { once: true },
          );
        });
      }
      return { finalized: false, aborted: true };
    },
    [store, checkpoint, saveContactTopics, saveResult],
  );

  observeRunProgressRef.current = observeRunProgress;

  const resumeFromCheckpoint = useCallback(
    async (
      projectId: number,
      language: OutputLanguage,
    ): Promise<{ resumed: boolean; fallbackToFullRun: boolean }> => {
      const { checkpoint: savedCheckpoint } = checkpoint.getCheckpoint(
        projectId,
        language,
      );
      if (!savedCheckpoint) {
        return { resumed: false, fallbackToFullRun: false };
      }
      if (!isResumeReadyCheckpoint(savedCheckpoint)) {
        console.warn(
          `[useAICheckRun] Incomplete checkpoint discarded for project ${projectId}:${language}`,
        );
        checkpoint.clearCheckpoint(projectId, language);
        return { resumed: false, fallbackToFullRun: true };
      }

      abortControllerRef.current?.abort();
      // 旧経路で始める実行は runId を持たない。前の実行の runId が残ると
      // 「今の実行を止めたつもりで前の実行を止める」になる。
      activeRunIdRef.current = null;
      const controller = new AbortController();
      abortControllerRef.current = controller;
      applyCheckpointToStore(projectId, language, savedCheckpoint, {
        status: "reconnecting",
        currentStep: "Resuming incomplete checks...",
        clearCurrentResult: true,
      });
      store.initPartialRun(projectId, savedCheckpoint.startedAt);
      // **経過時間はこの試行の開始から測る。** 表示の目的は「あとどれくらい
      // 待つか」なので、中断していた間は数えない。元の run の開始時刻から
      // 測ると、一晩置いて再開しただけで「まもなく完了します」に化ける。
      store.setStartedAt(Date.now());

      // 進捗イベントの解釈は observeRunProgress 側が持つ（開始した本人も
      // 途中から見に来た人も同じ経路を通す）。

      // **黙って抜けない。** ここへ来る前に status を "reconnecting"
      // （"Resuming incomplete checks..."）にしてあるので、何も表示を
      // 変えずに return すると画面はその文言のまま永久に止まる。
      // 実際にそうなった（進捗も出ず、押せるものも無い状態）。
      const bail = (reason: string) => {
        store.setDisconnected(reason);
        return { resumed: false, fallbackToFullRun: false };
      };

      try {
        const token = await authService.getIdToken();
        if (!token) return bail("Not authenticated");

        const remainingCheckIds =
          checkpoint.deriveRemainingCheckIds(savedCheckpoint);
        // **再開も開始と観測を分けた入口を通す。** 旧 `/api/aicheck/run` は
        // 案件を本文の mode からしか判断しないため、外部案件の再開を
        // `crm:<env>:<displayId>` として保存し、同じ案件の履歴が二つのキーに
        // 割れていた。新しい入口は登録済み案件のスコープを正として使う。
        const response = await fetch(apiUrl("/api/aicheck/runs"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(
            buildCheckpointResumeRequest(
              projectId,
              language,
              savedCheckpoint,
              remainingCheckIds,
            ),
          ),
          signal: controller.signal,
        });

        if (response.status === 409) {
          const conflict = await parseConflictResponse(response);
          if (
            conflict?.reason === "checkpoint_project_data_invalid" ||
            conflict?.reason === "checkpoint_ai_results_unusable"
          ) {
            console.warn(
              `[useAICheckRun] Checkpoint resume fell back to full run (${conflict.reason}) for project ${projectId}:${language}`,
            );
            checkpoint.clearCheckpoint(projectId, language);
            return { resumed: false, fallbackToFullRun: true };
          }
          throw new Error(
            conflict?.message ??
              `Unexpected server conflict while resuming (${conflict?.reason ?? "unknown"})`,
          );
        }
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error ?? `Server error: ${response.status}`);
        }

        checkpoint.clearCheckpoint(projectId, language);

        const started = (await response.json()) as { runId?: unknown };
        const startedRunId = String(started.runId ?? "");
        if (!startedRunId) {
          return bail("Server returned no runId");
        }
        activeRunIdRef.current = startedRunId;

        const outcome = await observeRunProgressRef.current?.(
          startedRunId,
          projectId,
          language,
          controller.signal,
          {
            // 表示はチェックポイントから組み直し済み。捨てると再開の意味が無い。
            resetProgress: false,
            announceReconnecting: false,
            saveContactTopics: true,
          },
        );
        if (!outcome || outcome.aborted) {
          // 利用者が止めた。表示は setAborted 側が受け持っている。
          return { resumed: false, fallbackToFullRun: false };
        }
        if (!outcome.finalized) {
          return bail("Connection closed before results were received");
        }

        // 結果の確定・保存は observeRunProgress が済ませている
        return { resumed: true, fallbackToFullRun: false };
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return { resumed: false, fallbackToFullRun: false };
        }
        store.setError(getErrorMessage(err, "Unknown error occurred"));
        return { resumed: false, fallbackToFullRun: false };
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [store, checkpoint],
  );

  const showCheckpointAsAborted = useCallback(
    (projectId: number, language: OutputLanguage): boolean => {
      const { checkpoint: savedCheckpoint } = checkpoint.getCheckpoint(
        projectId,
        language,
      );
      if (!savedCheckpoint) {
        return false;
      }
      if (!isResumeReadyCheckpoint(savedCheckpoint)) {
        console.warn(
          `[useAICheckRun] Incomplete checkpoint discarded for project ${projectId}:${language}`,
        );
        checkpoint.clearCheckpoint(projectId, language);
        return false;
      }

      const staleDirectives = toStaleNodeExecutionDirectives(
        savedCheckpoint.nodeExecutionDirectives,
      );
      applyCheckpointToStore(projectId, language, savedCheckpoint, {
        status: "aborted",
        currentStep: "Check aborted. Resume available.",
        nodeExecutionDirectives: staleDirectives,
        clearCurrentResult: true,
      });
      checkpoint.setRunStatus(
        projectId,
        language,
        savedCheckpoint.startedAt,
        "aborted",
      );
      checkpoint.setNodeExecutionState(
        projectId,
        language,
        savedCheckpoint.startedAt,
        {
          nodeStates: savedCheckpoint.nodeStates,
          nodeExecutionDirectives: staleDirectives,
          nodeControlVersion: savedCheckpoint.nodeControlVersion ?? 0,
        },
      );
      return true;
    },
    [checkpoint],
  );

  const runAdminControl = useCallback(
    async (
      projectId: number,
      language: OutputLanguage,
      action: AdminNodeAction,
      nodeId: string,
    ): Promise<
      | AdminControlSuccessResponse
      | { ok: false; reason: string; message?: string }
    > => {
      const token = await authService.getIdToken();
      if (!token) {
        return { ok: false, reason: "not_authenticated" };
      }

      const checkpointSnapshot = checkpoint.getCheckpoint(
        projectId,
        language,
      ).checkpoint;
      const clientResultSnapshot =
        (store.currentResult &&
        store.currentResult.projectId === projectId &&
        store.currentResult.language === language
          ? store.currentResult
          : null) ??
        (store.previousResult &&
        store.previousResult.projectId === projectId &&
        store.previousResult.language === language
          ? store.previousResult
          : null) ??
        getResult(projectId, language).result;

      const response = await fetch(apiUrl("/api/aicheck/run/admin/control"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId,
          language,
          action,
          target: { nodeId },
          expectedNodeControlVersion: store.runProgress.nodeControlVersion ?? 0,
          ...(checkpointSnapshot
            ? { browserCheckpoint: checkpointSnapshot }
            : undefined),
          ...(clientResultSnapshot ? { clientResultSnapshot } : undefined),
        }),
      });

      if (response.status === 409) {
        const conflict = await parseConflictResponse(response);
        if (conflict?.reason === "node_control_version_conflict") {
          try {
            const state = await fetchServerRunState(projectId, language, token);
            store.setNodeExecutionState({
              nodeStates: state.nodeStates,
              nodeExecutionDirectives: state.nodeExecutionDirectives,
              nodeControlVersion: state.nodeControlVersion,
            });
            const startedAt =
              store.partialRunData?.startedAt ??
              checkpointSnapshot?.startedAt ??
              new Date().toISOString();
            checkpoint.setNodeExecutionState(projectId, language, startedAt, {
              nodeStates: state.nodeStates,
              nodeExecutionDirectives: state.nodeExecutionDirectives,
              nodeControlVersion: state.nodeControlVersion,
            });
          } catch {
            // Best effort sync only.
          }
        }
        return {
          ok: false,
          reason: conflict?.reason ?? "unknown_conflict",
          message: conflict?.message,
        };
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return {
          ok: false,
          reason: "request_failed",
          message: String(errData.error ?? `Server error: ${response.status}`),
        };
      }

      return (await response.json()) as AdminControlSuccessResponse;
    },
    [
      checkpoint,
      getResult,
      store.currentResult,
      store.partialRunData?.startedAt,
      store.previousResult,
      store.runProgress.nodeControlVersion,
      store.setNodeExecutionState,
    ],
  );

  const adminRerunFromNode = useCallback(
    async (projectId: number, language: OutputLanguage, nodeId: string) => {
      const response = await runAdminControl(
        projectId,
        language,
        "rerun_from_node",
        nodeId,
      );
      if (!response.ok) return response;
      if (response.action !== "rerun_from_node") {
        return {
          ok: false as const,
          reason: "invalid_response",
          message: "Unexpected admin control response.",
        };
      }

      checkpoint.replaceCheckpoint(response.checkpointSeed);
      applyCheckpointToStore(projectId, language, response.checkpointSeed, {
        status: "reconnecting",
        currentStep: "Re-running selected node...",
        clearCurrentResult: true,
      });

      for (let attempt = 0; attempt < 10; attempt += 1) {
        const attached = await reconnectRun(projectId, language);
        if (attached) {
          return response;
        }
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      try {
        const token = await authService.getIdToken();
        if (token) {
          const state = await fetchServerRunState(projectId, language, token);
          if (state.status === "in_flight") {
            store.setDisconnected(
              "Admin rerun is still running on the server but reconnect could not attach.",
            );
            return {
              ok: false as const,
              reason: "run_not_found",
              message:
                "Admin rerun is still running on the server but reconnect could not attach.",
            };
          }
          if (state.status === "completed" && state.runId) {
            const result = await fetchRunResultById(state.runId, token);
            assertServerResultMetadata(result, "admin rerun recovery");
            const cacheableResult = withProjectDataComparison(result);
            store.completeRun(cacheableResult);
            saveResult(cacheableResult);
            checkpoint.clearCheckpoint(projectId, language);
            if (
              cacheableResult.status === "completed" &&
              !hasExecutionFailures(cacheableResult)
            ) {
              await releaseServerRunResult(state.runId, token);
            }
            return response;
          }
        }
      } catch (error) {
        console.warn(
          `[useAICheckRun] Admin rerun post-reconnect recovery failed: ${getErrorMessage(error)}`,
        );
      }

      const fallbackCheckpoint =
        checkpoint.getCheckpoint(projectId, language).checkpoint ??
        response.checkpointSeed;
      const fallbackMessage =
        "Admin rerun stopped before a recoverable server result was available. Resume from checkpoint is still available.";
      checkpoint.replaceCheckpoint({
        ...fallbackCheckpoint,
        lastKnownStatus: "aborted",
      });
      applyCheckpointToStore(projectId, language, fallbackCheckpoint, {
        status: "aborted",
        currentStep: fallbackMessage,
        clearCurrentResult: true,
      });
      return {
        ok: false as const,
        reason: "run_not_found",
        message: fallbackMessage,
      };
    },
    [checkpoint, reconnectRun, runAdminControl, saveResult, store],
  );

  const toggleAbortFromHere = useCallback(
    async (projectId: number, language: OutputLanguage, nodeId: string) => {
      const response = await runAdminControl(
        projectId,
        language,
        "toggle_abort_from_here",
        nodeId,
      );
      if (!response.ok) return response;
      if (response.action !== "toggle_abort_from_here") {
        return {
          ok: false as const,
          reason: "invalid_response",
          message: "Unexpected admin control response.",
        };
      }
      const startedAt =
        store.partialRunData?.startedAt ??
        checkpoint.getCheckpoint(projectId, language).checkpoint?.startedAt ??
        new Date().toISOString();
      store.setNodeExecutionState({
        nodeExecutionDirectives: response.nodeExecutionDirectives,
        nodeControlVersion: response.nodeControlVersion,
      });
      checkpoint.setNodeExecutionState(projectId, language, startedAt, {
        nodeExecutionDirectives: response.nodeExecutionDirectives,
        nodeControlVersion: response.nodeControlVersion,
      });
      return response;
    },
    [checkpoint, runAdminControl, store],
  );

  const abortNode = useCallback(
    async (projectId: number, language: OutputLanguage, nodeId: string) => {
      const response = await runAdminControl(
        projectId,
        language,
        "abort_node",
        nodeId,
      );
      if (!response.ok) return response;
      if (response.action !== "abort_node") {
        return {
          ok: false as const,
          reason: "invalid_response",
          message: "Unexpected admin control response.",
        };
      }
      const startedAt =
        store.partialRunData?.startedAt ??
        checkpoint.getCheckpoint(projectId, language).checkpoint?.startedAt ??
        new Date().toISOString();
      store.setNodeExecutionState({
        nodeExecutionDirectives: response.nodeExecutionDirectives,
        nodeControlVersion: response.nodeControlVersion,
      });
      store.mergeNodeStates({ [response.abortedNodeId]: "aborted" });
      checkpoint.setNodeExecutionState(projectId, language, startedAt, {
        nodeStates: {
          ...(useAICheckStore.getState().runProgress.nodeStates ?? {}),
          [response.abortedNodeId]: "aborted",
        },
        nodeExecutionDirectives: response.nodeExecutionDirectives,
        nodeControlVersion: response.nodeControlVersion,
      });
      return response;
    },
    [checkpoint, runAdminControl, store],
  );

  return {
    runCheck,
    runSelectedChecks,
    reconnectRun,
    observeRunProgress,
    resumeFromCheckpoint,
    showCheckpointAsAborted,
    adminRerunFromNode,
    toggleAbortFromHere,
    abortNode,
    abortRun,
    cancelRun,
    fetchProjectData,
    loadCachedResult,
    projectId: store.projectId,
    setProjectId: store.setProjectId,
    runProgress: store.runProgress,
    currentResult: store.currentResult,
    previousResult: store.previousResult,
    partialRunData: store.partialRunData,
    reset: store.reset,
  };
}
